import { GridCell, GridCellKind, GridColumn } from '@glideapps/glide-data-grid';

import { formatFinancialValue } from '@/shared/components/financial-values';
import { financialValueCopyData } from '@/shared/components/tables/FinancialSourceAttribution/utils';
import type { FinancialValueDisplayPreference } from '@/types/financialValueDisplay';
import { dayjs } from '@/utilities';

import type { DividendHistoryRow } from '../../financialSeries';

export type DividendHistoryColumnId =
    | 'exDividendDate'
    | 'declarationDate'
    | 'recordDate'
    | 'paymentDate'
    | 'frequency'
    | 'dividend'
    | 'adjustedDividend'
    | 'yield';

export type DividendHistoryGridColumn = GridColumn & { readonly id: DividendHistoryColumnId };

export interface DividendHistorySort {
    columnId: DividendHistoryColumnId;
    direction: 'asc' | 'desc';
}

export interface DividendHistoryCellColors {
    negative: string;
    positive: string;
}

export const DIVIDEND_HISTORY_COLUMNS: readonly DividendHistoryGridColumn[] = [
    { grow: 1, icon: 'TbCurrencyDollar', id: 'dividend', title: 'Dividend' },
    { grow: 1, icon: 'TbCurrencyDollar', id: 'adjustedDividend', title: 'Adju. Dividend' },
    { grow: 1, icon: 'TbPercentage', id: 'yield', title: '% Yield Change' },
    { grow: 1, icon: 'TbCalendarEvent', id: 'exDividendDate', title: 'Ex Date' },
    { grow: 1, icon: 'TbCalendarEvent', id: 'declarationDate', title: 'Declaration Date' },
    { grow: 1, icon: 'TbCalendarEvent', id: 'recordDate', title: 'Record Date' },
    { grow: 1, icon: 'TbCalendarEvent', id: 'paymentDate', title: 'Pay Date' },
    { grow: 1, icon: 'TbCalendarBolt', id: 'frequency', title: 'Frequency' },
];

const currencyFormatter = new Intl.NumberFormat('en-US', {
    currency: 'USD',
    currencySign: 'accounting',
    maximumFractionDigits: 6,
    minimumFractionDigits: 2,
    style: 'currency',
});

const yieldFormatter = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
});

const textCollator = new Intl.Collator('en-US', { numeric: true, sensitivity: 'base' });

const compareDate = (left?: string, right?: string): number => {
    const leftTimestamp = left ? dayjs(left).valueOf() : Number.NEGATIVE_INFINITY;
    const rightTimestamp = right ? dayjs(right).valueOf() : Number.NEGATIVE_INFINITY;

    return leftTimestamp - rightTimestamp;
};

const compareOptionalNumber = (left: number | null, right: number | null): number =>
    (left ?? Number.NEGATIVE_INFINITY) - (right ?? Number.NEGATIVE_INFINITY);

const compareRows = (left: DividendHistoryRow, right: DividendHistoryRow, columnId: DividendHistoryColumnId) => {
    switch (columnId) {
        case 'exDividendDate':
        case 'declarationDate':
        case 'recordDate':
        case 'paymentDate':
            return compareDate(left[columnId], right[columnId]);

        case 'dividend':
        case 'adjustedDividend':
        case 'yield':
            return compareOptionalNumber(left[columnId], right[columnId]);

        case 'frequency':
            return textCollator.compare(left.frequency ?? '', right.frequency ?? '');
    }
};

export const sortDividendHistoryRows = (
    rows: readonly DividendHistoryRow[],
    sort: DividendHistorySort,
): DividendHistoryRow[] => {
    const multiplier = sort.direction === 'asc' ? 1 : -1;

    return [...rows].sort((left, right) => {
        const result = compareRows(left, right, sort.columnId);

        return result === 0 ? left.key.localeCompare(right.key) : result * multiplier;
    });
};

const formatDate = (value?: string): string => (value ? dayjs(value).format('MMM DD, YYYY') : '—');

const formatCurrency = (value: number | null, preference?: FinancialValueDisplayPreference): string =>
    value === null
        ? '—'
        : preference
          ? formatFinancialValue(value, preference, { scalable: false })
          : currencyFormatter.format(value);

const formatYield = (value: number | null): string => (value === null ? '—' : `${yieldFormatter.format(value)}%`);

const textCell = (value: string): GridCell => ({
    allowOverlay: false,
    data: value,
    displayData: value,
    kind: GridCellKind.Text,
    readonly: true,
});

const numberCell = (value: number | null, displayData: string, textColor?: string): GridCell => ({
    allowOverlay: false,
    contentAlign: 'right',
    copyData: financialValueCopyData(value ?? undefined),
    data: value ?? undefined,
    displayData,
    kind: GridCellKind.Number,
    readonly: true,
    themeOverride: textColor ? { textDark: textColor } : undefined,
});

export const createDividendHistoryGridCell = (
    row: DividendHistoryRow,
    columnId: DividendHistoryColumnId,
    locked: boolean,
    colors?: DividendHistoryCellColors,
    financialValueDisplay?: FinancialValueDisplayPreference,
): GridCell => {
    if (locked && columnId !== 'exDividendDate') {
        return {
            allowOverlay: false,
            data: ['Upgrade to Access'],
            kind: GridCellKind.Bubble,
        };
    }

    switch (columnId) {
        case 'exDividendDate':
        case 'declarationDate':
        case 'recordDate':
        case 'paymentDate':
            return textCell(formatDate(row[columnId]));

        case 'frequency':
            return textCell(row.frequency ?? '—');

        case 'dividend':
        case 'adjustedDividend':
            return numberCell(row[columnId], formatCurrency(row[columnId], financialValueDisplay));

        case 'yield': {
            const textColor =
                row.yield === null || row.yield === 0 ? undefined : row.yield > 0 ? colors?.positive : colors?.negative;

            return numberCell(row.yield, formatYield(row.yield), textColor);
        }
    }
};

export const createDividendHistoryGridColumns = (sort: DividendHistorySort): DividendHistoryGridColumn[] =>
    DIVIDEND_HISTORY_COLUMNS.map((column) => ({
        ...column,
        title: `${column.title}${sort.columnId === column.id ? (sort.direction === 'asc' ? ' ↑' : ' ↓') : ''}`,
    }));
